import CtaIcon from '../ui/CtaIcon';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/ApiService';
import Footer from '../Footer';
import PassportCard from '../passport/PassportCard';
import { defaultCardDesign, type PassportCard as Card } from '../../services/api/passportCards';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';
import SponsoredPlacement from './SponsoredPlacement';
import {
  normalizeSponsorship,
  type AdCreative,
  type HomeVehicleCard,
  type PublicHomeData,
} from './homeTypes';
import './bilt-home.css';
import './bilt-cinematic.css';
import { useScrollScore } from '../../hooks/useScrollScore';
import { vehicleSourceCard } from '../passport/sourceCards';
import { homeVehicleToCardProps } from './homeTypes';
import HomePassportReveal from './HomePassportReveal';
const HOME_DEMO_BUILD_SLUGS = new Set(['evowagon-v3', 'cokecar']);

function isHomeDemoBuild(vehicle: HomeVehicleCard): boolean {
  const slug = String(vehicle.public_slug || '').toLowerCase();
  const label = `${vehicle.nickname || ''} ${vehicle.display_name || ''}`.toLowerCase();
  return (
    HOME_DEMO_BUILD_SLUGS.has(slug) ||
    (vehicle.make.toLowerCase() === 'subaru' && label.includes('coke')) ||
    label.includes('evo wagon v3')
  );
}

function buildsFrom(data: PublicHomeData | null): HomeVehicleCard[] {
  const all = [
    data?.featured_build?.vehicle,
    ...(data?.recent_builds || []),
    ...(data?.recently_awarded || []),
  ];
  const unique = all
    .filter((v, i): v is HomeVehicleCard => !!v?.id && all.findIndex((c) => c?.id === v.id) === i)
  const demos = unique.filter(isHomeDemoBuild);
  // Keep fixtures and a future empty demo response useful while production is
  // seeded: known demo builds win whenever they are present.
  return (demos.length > 0 ? demos : unique).slice(0, 2);
}
export default function PublicHomePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PublicHomeData | null>(null);
  const [ad, setAd] = useState<AdCreative | null>(null);
  const [selected, setSelected] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [back, setBack] = useState(false);
  const scrollRoot = useScrollScore(data);
  useEffect(() => {
    let active = true;
    void Promise.all([
      api.getHomePublic().catch(() => null),
      api.getAdPlacement('homepage_banner').catch(() => null),
    ]).then(([home, placement]) => {
      if (active) {
        setData(home);
        setAd(normalizeSponsorship(home?.sponsorship) || placement);
      }
    });
    void api.trackEvent('home_viewed', {
      metadata: { audience: 'public', experience: 'passport_showcase' },
    });
    return () => {
      active = false;
    };
  }, []);
  const builds = useMemo(() => buildsFrom(data), [data]);
  const vehicle = builds[selected] || builds[0];
  const title = vehicle
    ? vehicle.nickname || vehicle.display_name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`
    : 'A story worth keeping';
  const image = resolvePublicFileUrl(vehicle?.image_url);
  const slug = vehicle ? resolveVehicleNavSlug(vehicle) : '';
  const path =
    vehicle?.owner_username && slug
      ? vehicleProfilePath(vehicle.owner_username, slug)
      : '/discover';
  const card: Card = vehicle
    ? vehicleSourceCard(homeVehicleToCardProps(vehicle))
    : {
        id: null,
        title,
        issuer: 'Bilt:Spec',
        subject_type: 'vehicle',
        subject_id: 0,
        status: 'preview',
        rules: { evidence: 'qr', cap: null },
        design: defaultCardDesign,
        subject: null,
      };
  function join(surface: string) {
    void api.trackEvent('home_cta_clicked', { metadata: { cta: 'join_bilt', surface } });
    navigate('/register', { state: { from: '/passport' } });
  }
  function pick(i: number) {
    setSelected(i);
    setRevealed(false);
    setBack(false);
  }
  return (
    <div
      className="bilt-home"
      data-demo-build-count={builds.length}
      ref={scrollRoot}
      style={{ '--bh-material': 'url(/images/passport/card-material.jpg)' } as React.CSSProperties}
    >
      <div className="bh-content">
        <section
          className="bh-hero"
          data-sc-act="flow"
          data-sc-device="parallax"
          aria-labelledby="bh-title"
        >
          <div className="bh-hero-copy bh-hero-callout">
            <p className="bh-eyebrow">For the love of the car.</p>
            <h1 id="bh-title">
              Some cars
              <br />
              stay with you.
              <br />
              <span>Keep them.</span>
            </h1>
            <p className="bh-lead">
              Scan the QR. Discover the build. Keep its card in your Bilt Passport. You don’t need
              to own a car to belong here.
            </p>
            <div className="bh-actions">
              <button className="bh-button" onClick={() => join('hero')}>
                Join Bilt{' '}
                <span aria-hidden="true">
                  <CtaIcon direction="open" />
                </span>
              </button>
              <a className="bh-text-link" href="#try-passport">
                Try Passport
              </a>
            </div>
            <p className="bh-access">Have an invitation? Your collection starts here.</p>
          </div>
          <div className="bh-hero-scene" aria-hidden="true">
            <img
              className="bh-material"
              src="/images/passport/card-material.jpg"
              alt=""
              width="1536"
              height="1024"
            />
            <div className="bh-hero-photo">
              <img
                src={image || '/images/brand/home-build-record-wide.jpg'}
                alt=""
                width="1400"
                height="1000"
                decoding="async"
              />
            </div>
            <div className="bh-hero-card">
              <PassportCard card={card} demo />
            </div>
            <div className="bh-hero-lip" />
            <div className="bh-hero-light" />
            <div className="bh-object-label">
              <span>{vehicle ? 'A build on Bilt:Spec' : 'The car is only the beginning'}</span>
              <strong>
                {vehicle
                  ? `${vehicle.year} ${vehicle.make} ${vehicle.model}`
                  : 'Scan. Discover. Collect.'}
              </strong>
            </div>
          </div>
          <div className="bh-hero-foot">
            <span>A passport to car culture.</span>
          </div>
        </section>
        <section
          className="bh-demo bh-build-lab bh-section"
          id="try-passport"
          data-sc-act="flow"
          data-sc-device="reveal"
          aria-labelledby="bh-demo-title"
        >
          <div className="bh-section-label">
            <span>The find</span>
            <span>Bilt Passport</span>
          </div>
          <div className="bh-demo-layout">
            <div className="bh-demo-stage" data-state={revealed ? 'revealed' : 'ready'}>
              <div className="bh-demo-plate" aria-hidden="true">
                <img
                  src={image || '/images/brand/home-story-workshop.jpg'}
                  alt=""
                  loading="lazy"
                  width="1400"
                  height="1000"
                />
              </div>
              <div className={`bh-demo-card bh-stat-card ${revealed ? 'is-revealed' : ''}`}>
                <PassportCard
                  key={vehicle?.id || 'sample'}
                  card={card}
                  demo
                  back={back}
                  readableStory
                  backLabel="Car build statistics"
                  actions={vehicle ? { source: { href: path, label: "View build" } } : undefined}
                  onFlip={() => setBack((v) => !v)}
                />
              </div>
              <div className="bh-demo-controls">
                {!revealed && (
                  <button
                    className="bh-button"
                    disabled={!vehicle}
                    onClick={() => {
                      setRevealed(true);
                      setBack(true);
                      void api.trackEvent('home_passport_demo_revealed', {
                        vehicle_id: vehicle?.id,
                        metadata: { demo: true },
                      });
                    }}
                  >
                    Reveal this card{' '}
                    <span aria-hidden="true">
                      <CtaIcon direction="open" />
                    </span>
                  </button>
                )}
                <span aria-live="polite">
                  {revealed
                    ? 'A sample reveal. Your real collection starts with a scan.'
                    : vehicle
                      ? 'Try a sample reveal. No signup needed.'
                      : 'Public build previews appear here when available.'}
                </span>
              </div>
            </div>
            <div className="bh-demo-copy">
              <h2 id="bh-demo-title">
                That one.
                <br />
                <span>Keep it.</span>
              </h2>
              <p>
                The car you walked across the lot to see. The build with the detail you can’t stop
                thinking about. Now there’s something to take home.
              </p>
              <p>
                A Bilt QR opens the story and adds a card to your Passport. At participating events
                and places, discover editions that mark the moment you were there.
              </p>
              <div className="bh-choice">
                <div className="bh-choice-heading">
                  <span className="bh-eyebrow">Pick a real build. Try its card.</span>
                  <span className="bh-choice-count">{builds.length} live examples</span>
                </div>
                <div className="bh-build-options">
                  {builds.slice(0, 4).map((v, i) => (
                    <button
                      key={v.id}
                      aria-pressed={selected === i}
                      aria-label={`Preview ${v.year} ${v.make} ${v.model}`}
                      onClick={() => pick(i)}
                    >
                      {v.image_url && (
                        <img
                          src={resolvePublicFileUrl(v.image_url) || ''}
                          alt=""
                          width="128"
                          height="96"
                          loading="lazy"
                        />
                      )}
                      <span>{v.make}</span>
                    </button>
                  ))}
                </div>
              </div>
              {revealed && (
                <div className="bh-story-receipt" data-side={back ? 'back' : 'front'}>
                  <span className="bh-eyebrow">
                    {back ? 'Behind the card' : 'The build you picked'}
                  </span>
                  <p>{back ? card.design.story : title}</p>
                  <Link className="bh-text-link" to={path}>
                    Explore the build <CtaIcon />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
        <HomePassportReveal buildCard={card} />
        <section
          className="bh-build bh-section"
          id="behind-the-build"
          data-sc-act="flow"
          data-sc-device="flow"
          aria-labelledby="bh-build-title"
        >
          <div className="bh-section-label">
            <span>The story</span>
            <span>Built, not just seen</span>
          </div>
          <div className="bh-build-heading">
            <h2 id="bh-build-title">
              The card gets you curious.
              <br />
              <span>The build pulls you in.</span>
            </h2>
            <p>
              Someone made every choice you’re looking at. Follow the parts, the progress, the
              people and the history, all connected to one build.
            </p>
          </div>
          <div className="bh-build-object">
            <Link to={path} className="bh-build-image" aria-label={`Open ${title}`}>
              <img
                src={image || '/images/brand/home-story-workshop.jpg'}
                alt={vehicle ? title : 'A car in a workshop'}
                width="1400"
                height="1000"
                loading="lazy"
              />
              <span className="bh-image-action" aria-hidden="true">
                <CtaIcon direction="open" />
              </span>
            </Link>
            <div className="bh-build-notes">
              <span className="bh-eyebrow">
                {vehicle ? 'The build you picked' : 'For the people who build'}
              </span>
              <h3>{title}</h3>
              {vehicle && <p>By @{vehicle.owner_username}</p>}
              <dl>
                {vehicle?.modification_count != null && (
                  <div>
                    <dt>Documented modifications</dt>
                    <dd>{vehicle.modification_count}</dd>
                  </div>
                )}
                {vehicle?.engine && (
                  <div>
                    <dt>Engine</dt>
                    <dd>{vehicle.engine}</dd>
                  </div>
                )}
                {vehicle?.drivetrain && (
                  <div>
                    <dt>Drivetrain</dt>
                    <dd>{vehicle.drivetrain}</dd>
                  </div>
                )}
              </dl>
              <Link className="bh-text-link" to={path}>
                Explore the build <CtaIcon direction="open" />
              </Link>
              <div className="bh-builder-note">
                <strong>Building something of your own?</strong>
                <p>
                  Give it a home. Record the work, keep its history together, and share its story
                  through one QR.
                </p>
                <button className="bh-text-link" onClick={() => join('builder')}>
                  Bring your build to Bilt <CtaIcon direction="open" />
                </button>
              </div>
            </div>
          </div>
        </section>
        <section
          className="bh-community"
          data-sc-act="flow"
          data-sc-device="pan"
          aria-labelledby="bh-community-title"
        >
          <div className="bh-community-photo">
            <img
              src="/images/brand/home-event-chapter-wide.jpg"
              alt="Cars and enthusiasts gathered at a meet"
              width="1800"
              height="1200"
              loading="lazy"
            />
          </div>
          <div className="bh-community-copy">
            <h2 id="bh-community-title">
              A reason to
              <br />
              say, “See you
              <br />
              <span>at the next one.”</span>
            </h2>
            <p>
              The meet. The club. The shop everyone knows. Bilt connects the cars you love with the
              people who bring them together.
            </p>
            <div className="bh-actions">
              <Link className="bh-text-link" to="/events">
                Find your next event <CtaIcon direction="open" />
              </Link>
              <Link className="bh-text-link" to="/clubs">
                Meet the clubs <CtaIcon direction="open" />
              </Link>
            </div>
          </div>
        </section>
        {ad && (
          <div className="bh-sponsored">
            <SponsoredPlacement creative={ad} />
          </div>
        )}
        <section
          className="bh-close bh-section"
          data-sc-act="flow"
          data-sc-device="flow"
          aria-labelledby="bh-close-title"
        >
          <div className="bh-section-label">
            <span>Your next chapter</span>
            <span>Bilt:Spec</span>
          </div>
          <div className="bh-close-layout">
            <div>
              <h2 id="bh-close-title">
                Come for the cars.
                <br />
                <span>Find your people.</span>
              </h2>
              <p>
                Collect what moves you. Build something worth sharing.
                <br />
                There’s a place for you here.
              </p>
              <div className="bh-actions">
                <button className="bh-button" onClick={() => join('final')}>
                  Join Bilt <CtaIcon direction="open" />
                </button>
                <Link className="bh-text-link" to="/discover">
                  Browse builds first
                </Link>
              </div>
              <p className="bh-access">
                Bilt is currently invitation only. Already a member?{' '}
                <Link to="/login">Sign in.</Link>
              </p>
            </div>
            <div className="bh-close-mark" aria-hidden="true">
              <img src="/brand/logo-lockup-nav-2x.png" alt="" width="440" height="100" />
              <span>The build stays with the car.</span>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
